import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { servicesAPI } from '../api';
import { useLanguage } from '../context/LanguageContext';
import BrandsCarousel from '../components/BrandsCarousel';
import SectionHeading from '../components/SectionHeading';
import { Reveal } from '../components/Reveal';
import {
  Check, Layers, Lightbulb, Volume2, Home, Film,
  Sun, Palette, Zap, Sparkles,
  Clapperboard, Music, Tv, Headphones,
  Shield, Lock, LockKeyhole, Thermometer, PanelTop,
  Projector, Speaker, Armchair, Smartphone, ArrowRight, ArrowUpRight
} from 'lucide-react';

const EASE = [0.16, 1, 0.3, 1];

const iconMap = {
  '1': Layers,
  '2': Lightbulb,
  '3': Volume2,
  '4': Home,
  '5': Film,
};

const ICON_FALLBACK = [Layers, Lightbulb, Volume2, Home, Film];

const subServiceIconMap = {
  'Iluminación Integrada': Lightbulb,
  'Audio Multizona': Volume2,
  'Climatización': Thermometer,
  'Seguridad': Lock,
  'Natural Light': Sun,
  'Color Control': Palette,
  'Energy Saving': Zap,
  'Smart Scenes': Sparkles,
  'Home Cinema': Clapperboard,
  'Multi-Room Audio': Music,
  'Video Streaming': Tv,
  'Sound Design': Headphones,
  'Security System': Shield,
  'Smart Locks': LockKeyhole,
  'Climate Control': Thermometer,
  'Smart Blinds': PanelTop,
  'Proyección Laser': Projector,
  'Dolby Atmos': Speaker,
  'Butacas Premium': Armchair,
  'Control Total': Smartphone,
};

const AllServicesPage = () => {
  const { t, language } = useLanguage();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  const serviceTranslations = {
    '1': { es: 'Proyectos Integrales', en: 'Integral Projects' },
    '2': { es: 'Iluminación Inteligente', en: 'Smart Lighting' },
    '3': { es: 'Sistemas Audiovisuales', en: 'Audiovisual Systems' },
    '4': { es: 'Automatización y Control', en: 'Automation & Control' },
    '5': { es: 'Cines Privados', en: 'Private Cinemas' },
  };

  const descTranslations = {
    '1': { es: 'Desarrollamos soluciones completas que abarcan todos los aspectos de la automatización moderna.', en: 'We develop complete solutions covering all aspects of modern automation.' },
    '2': { es: 'Diseñamos sistemas de iluminación que transforman ambientes y optimizan el consumo energético.', en: 'We design lighting systems that transform environments and optimize energy consumption.' },
    '3': { es: 'Implementamos tecnología de entretenimiento de vanguardia que ofrece experiencias inmersivas.', en: 'We implement cutting-edge entertainment technology that delivers immersive experiences.' },
    '4': { es: 'Instalamos sistemas de domótica que centralizan el control de todos los elementos de su espacio.', en: 'We install home automation systems that centralize control of all elements in your space.' },
    '5': { es: 'Creamos experiencias cinematográficas exclusivas en la comodidad de su hogar.', en: 'We create exclusive cinematic experiences in the comfort of your home.' },
  };

  const subServiceTranslations = {
    'Iluminación Integrada': { es: 'Iluminación Integrada', en: 'Integrated Lighting' },
    'Audio Multizona': { es: 'Audio Multizona', en: 'Multi-Zone Audio' },
    'Climatización': { es: 'Climatización', en: 'Climate Control' },
    'Seguridad': { es: 'Seguridad', en: 'Security' },
    'Natural Light': { es: 'Luz Natural', en: 'Natural Light' },
    'Color Control': { es: 'Control de Color', en: 'Color Control' },
    'Energy Saving': { es: 'Ahorro Energético', en: 'Energy Saving' },
    'Smart Scenes': { es: 'Escenas Inteligentes', en: 'Smart Scenes' },
    'Home Cinema': { es: 'Cine en Casa', en: 'Home Cinema' },
    'Multi-Room Audio': { es: 'Audio Multiroom', en: 'Multi-Room Audio' },
    'Video Streaming': { es: 'Video Streaming', en: 'Video Streaming' },
    'Sound Design': { es: 'Diseño de Sonido', en: 'Sound Design' },
    'Security System': { es: 'Sistema de Seguridad', en: 'Security System' },
    'Smart Locks': { es: 'Cerraduras Inteligentes', en: 'Smart Locks' },
    'Climate Control': { es: 'Control Climático', en: 'Climate Control' },
    'Smart Blinds': { es: 'Persianas Inteligentes', en: 'Smart Blinds' },
    'Proyección Laser': { es: 'Proyección Láser', en: 'Laser Projection' },
    'Dolby Atmos': { es: 'Dolby Atmos', en: 'Dolby Atmos' },
    'Butacas Premium': { es: 'Butacas Premium', en: 'Premium Seats' },
    'Control Total': { es: 'Control Total', en: 'Total Control' },
  };

  useEffect(() => {
    const loadServices = async () => {
      try {
        const data = await servicesAPI.getAll();
        setServices(data);
        setLoading(false);
      } catch (error) {
        console.error('Error loading services:', error);
        setLoading(false);
      }
    };
    loadServices();
  }, []);

  useEffect(() => {
    if (!loading && window.location.hash) {
      setTimeout(() => {
        const element = document.querySelector(window.location.hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [loading]);

  const serviceDetails = [
    {
      id: '1', title: 'Proyectos Integrales',
      mainImage: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200&q=80',
      features: language === 'es'
        ? ['Diseño arquitectónico integrado', 'Gestión completa del proyecto', 'Coordinación de todos los sistemas', 'Control centralizado único', 'Integración perfecta', 'Documentación completa']
        : ['Integrated architectural design', 'Complete project management', 'Coordination of all systems', 'Single centralized control', 'Perfect integration', 'Complete documentation'],
      subServices: [{ name: 'Iluminación Integrada' }, { name: 'Audio Multizona' }, { name: 'Climatización' }, { name: 'Seguridad' }],
    },
    {
      id: '2', title: 'Iluminación Inteligente',
      mainImage: 'https://images.unsplash.com/photo-1524484485831-a92ffc0de03f?w=1200&q=80',
      features: ['Control de intensidad y color', 'Escenas personalizadas', 'Automatización por horarios', 'Sensores de presencia', 'Control por voz', 'Eficiencia energética 70%'],
      subServices: [{ name: 'Natural Light' }, { name: 'Color Control' }, { name: 'Energy Saving' }, { name: 'Smart Scenes' }],
    },
    {
      id: '3', title: 'Sistemas Audiovisuales',
      mainImage: 'https://images.unsplash.com/photo-1593784991095-a205069470b6?w=1200&q=80',
      features: ['Home cinema 4K/8K', 'Audio multihabitación', 'Streaming integrado', 'Acústica profesional', 'Altavoces invisibles', 'Control unificado'],
      subServices: [{ name: 'Home Cinema' }, { name: 'Multi-Room Audio' }, { name: 'Video Streaming' }, { name: 'Sound Design' }],
    },
    {
      id: '4', title: 'Automatización y Control',
      mainImage: 'https://images.unsplash.com/photo-1670222783941-c1bee0e32c85?w=1200&q=80',
      features: language === 'es'
        ? ['Control de climatización', 'Seguridad integrada', 'Persianas motorizadas', 'Riego automático', 'Control remoto total', 'Notificaciones en tiempo real']
        : ['Climate control', 'Integrated security', 'Motorized blinds', 'Automatic irrigation', 'Total remote control', 'Real-time notifications'],
      subServices: [{ name: 'Security System' }, { name: 'Smart Locks' }, { name: 'Climate Control' }, { name: 'Smart Blinds' }],
    },
    {
      id: '5', title: 'Cines Privados',
      mainImage: 'https://images.unsplash.com/photo-1710131459450-7c384b8be18f?w=1200&q=80',
      features: ['Proyección 4K/8K laser', 'Audio Dolby Atmos 7.1.4', 'Butacas motorizadas premium', 'Acústica profesional', 'Control ambiental total', 'Streaming integrado'],
      subServices: [{ name: 'Proyección Laser' }, { name: 'Dolby Atmos' }, { name: 'Butacas Premium' }, { name: 'Control Total' }],
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center pt-20">
        <p className="text-white/40">{t('services.loading') || 'Loading...'}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian">
      {/* Hero Section */}
      <section className="relative h-[62vh] min-h-[480px] flex items-center overflow-hidden noise-overlay">
        <div className="absolute inset-0">
          <motion.img
            src="https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=1600&q=85"
            alt="Servicios"
            className="w-full h-full object-cover"
            initial={{ scale: 1.12, opacity: 0 }}
            animate={{ scale: 1, opacity: 0.4 }}
            transition={{ duration: 1.5, ease: EASE }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-obsidian via-obsidian/75 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-obsidian/40" />
        </div>
        <div className="absolute -top-16 right-0 w-[34rem] h-[34rem] bg-brand/15 rounded-full blur-[140px]" />

        <div className="relative z-10 container mx-auto px-6 lg:px-10">
          <motion.p className="overline mb-5" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE, delay: 0.2 }}>
            {t('nav.servicios')}
          </motion.p>
          <motion.h1
            className="font-heading text-5xl md:text-6xl lg:text-7xl font-extralight text-white tracking-tight leading-[0.95] max-w-3xl"
            initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE, delay: 0.3 }}
          >
            {t('services.pageTitle')}
          </motion.h1>
          <motion.p className="mt-6 text-lg text-white/60 max-w-2xl leading-relaxed" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE, delay: 0.45 }}>
            {t('services.pageSubtitle')}
          </motion.p>
        </div>
      </section>

      {/* Services Overview Grid */}
      <section className="py-24 md:py-28 bg-obsidian">
        <div className="container mx-auto px-6 lg:px-10">
          <SectionHeading overline={t('services.ourSpecialties')} title={t('services.allServices')} />

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-5">
            {services.map((service, index) => {
              const Icon = iconMap[service.id] || ICON_FALLBACK[index % ICON_FALLBACK.length];
              const title = serviceTranslations[service.id]?.[language] || service.title;
              const desc = descTranslations[service.id]?.[language] || service.description;
              return (
                <motion.a
                  key={service.id}
                  href={`#servicio-${service.id}`}
                  data-testid={`service-overview-${service.id}`}
                  className="glass-card hover-lift p-8 rounded-2xl cursor-pointer group block"
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.6, ease: EASE, delay: index * 0.07 }}
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById(`servicio-${service.id}`)?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  <div className="w-12 h-12 rounded-xl bg-brand/10 border border-brand/20 flex items-center justify-center text-brand-light mb-6 group-hover:bg-brand group-hover:text-white transition-all duration-400">
                    <Icon size={24} strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading text-lg font-medium text-white mb-3 group-hover:text-brand-light transition-colors">{title}</h3>
                  <p className="text-white/50 text-sm leading-relaxed mb-5">{desc.substring(0, 90)}...</p>
                  <span className="inline-flex items-center gap-1 text-brand-light text-xs uppercase tracking-wider">
                    {t('services.viewDetails')} <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </span>
                </motion.a>
              );
            })}
          </div>
        </div>
      </section>

      {/* Core Services Title */}
      <section className="py-14 border-t border-white/10 bg-obsidian">
        <div className="container mx-auto px-6 lg:px-10">
          <Reveal className="flex items-center gap-4">
            <div className="w-1 h-12 bg-brand"></div>
            <h2 className="font-heading text-2xl md:text-3xl font-extralight text-white uppercase tracking-wider">
              {t('services.detailsTitle')}
            </h2>
          </Reveal>
        </div>
      </section>

      {/* Services Sections */}
      {serviceDetails.map((detail, index) => {
        const service = services.find((s) => s.id === detail.id);
        const isReversed = index % 2 !== 0;
        const title = serviceTranslations[detail.id]?.[language] || detail.title;
        const desc = descTranslations[detail.id]?.[language] || service?.description;

        return (
          <section
            key={detail.id}
            id={`servicio-${detail.id}`}
            className={`py-24 ${index % 2 === 0 ? 'bg-obsidian' : 'bg-surface'}`}
          >
            <div className="container mx-auto px-6 lg:px-10">
              <div className="grid md:grid-cols-2 gap-12 lg:gap-16 items-center">
                <Reveal className={`space-y-8 ${isReversed ? 'md:order-2' : ''}`}>
                  <div>
                    <h3 className="font-heading text-3xl md:text-4xl font-light text-white mb-4 tracking-tight">{title}</h3>
                    <p className="text-white/55 text-lg leading-relaxed">
                      {desc || (language === 'es' ? 'Descripción del servicio' : 'Service description')}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    {detail.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <div className="text-brand-light mt-0.5"><Check size={16} /></div>
                        <span className="text-white/55 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    {detail.subServices.map((sub, idx) => {
                      const SubIcon = subServiceIconMap[sub.name] || Home;
                      const subName = subServiceTranslations[sub.name]?.[language] || sub.name;
                      return (
                        <div key={idx} className="glass-card p-3 rounded-lg flex items-center gap-3">
                          <div className="text-brand-light"><SubIcon size={18} strokeWidth={1.5} /></div>
                          <span className="text-white/90 text-sm">{subName}</span>
                        </div>
                      );
                    })}
                  </div>

                  <Link to="/contacto" className="btn-primary inline-flex items-center gap-2 px-8 py-3.5 rounded-sm text-sm uppercase tracking-wider font-medium group">
                    {t('services.moreInformation')}
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Reveal>

                <motion.div
                  className={`relative ${isReversed ? 'md:order-1' : ''}`}
                  initial={{ opacity: 0, scale: 1.05 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 1, ease: EASE }}
                >
                  <div className="absolute -inset-4 bg-brand/10 rounded-3xl blur-2xl" />
                  <img src={detail.mainImage} alt={title} className="relative rounded-2xl w-full h-[480px] object-cover shadow-2xl" />
                </motion.div>
              </div>
            </div>
          </section>
        );
      })}

      {/* Brands / Partners */}
      <BrandsCarousel background="bg-obsidian" />

      {/* CTA Section */}
      <section className="py-24 bg-surface border-t border-white/10">
        <div className="container mx-auto px-6 lg:px-10">
          <Reveal className="glass-card p-12 md:p-16 rounded-3xl text-center max-w-4xl mx-auto">
            <h2 className="font-heading text-3xl md:text-4xl font-light text-white mb-6 tracking-tight">{t('cta.readyToTransform')}</h2>
            <p className="text-white/55 text-lg mb-10 max-w-2xl mx-auto">{t('cta.contactUs')}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contacto" className="btn-primary px-10 py-4 rounded-sm text-sm uppercase tracking-wider font-medium">{t('cta.requestConsultation')}</Link>
              <Link to="/proyectos" className="btn-ghost px-10 py-4 rounded-sm text-sm uppercase tracking-wider font-medium">{t('nav.proyectos')}</Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default AllServicesPage;
